/**
 * NotificationsListHeaderRow — the "Updates" section header of the Inbox list.
 *
 * Flutter source of truth: `NotificationsListHeader`
 * (`lib/presentation/screens/messages/widgets/notifications_list_header.dart:12-29`).
 *
 * Renders nothing when the notifications list is both empty and exhausted
 * (`!hasMoreItems && !hasItems`); otherwise a left-aligned "Updates" label.
 *
 * Token mapping (Flutter padding `left:16, top:8`, `notificationsHeaderStyle`:
 * `disabledTextColor`, size 14, weight 400):
 *   - `left:16`  → `responsive.spacing.lg` (16).
 *   - `top:8`    → `responsive.spacing.sm` (8).
 *   - color      → `theme.text.secondary` (the web convention for Flutter's
 *                  muted disabled-text header, matching the chat header).
 *   - size 14    → `responsive.font.sm`.
 *   - weight 400 → `responsive.fontWeight.regular`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface NotificationsListHeaderRowProps {
  hasMoreItems: boolean;
  hasItems: boolean;
}

export const NotificationsListHeaderRow: React.FC<NotificationsListHeaderRowProps> = ({
  hasMoreItems,
  hasItems,
}) => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter renders `Container()` (nothing) when empty-and-exhausted.
  if (!hasMoreItems && !hasItems) return null;

  return (
    <div
      style={{
        paddingLeft: responsive.spacing.lg,
        paddingTop: responsive.spacing.sm,
      }}
    >
      <span
        style={{
          display: 'block',
          textAlign: 'left',
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
          fontWeight: responsive.fontWeight.regular,
        }}
      >
        {l10n.inbox.updatesHeader}
      </span>
    </div>
  );
};
